<template>
  <div
    id="app"
    :style="customStyle"
  >
    <div
      v-if="!origemError"
      style="height: auto;"
    >
      <transition
        mode="out-in"
        name="fade"
        style="height: auto;"
        v-if="!loading && !request"
      >
        <router-view style="height: auto;"/>
      </transition>

      <ErrorHandler
        v-if="request"
        :request="request"
        @click="reload"
      />
    </div>

    <ErrorAlerta
      v-else
      :showBtn="errorConfig.showBtn"
      :texto="errorConfig.texto"
      :titulo="errorConfig.titulo"
    />
  </div>
</template>

<script>
import status from '@/mixins/status'
import ResizeObserver from 'resize-observer-polyfill'
import { mapGetters, mapMutations, mapState } from 'vuex'
import { decodeEmbedData } from '@/utils/utils'
import { axiosAPI, ConfiguradorResource, PlataformaBeneficiarioResource } from '@/API'
import ErrorHandler from '@/components/Error/ErrorHandler'
import ErrorAlerta from '@/components/Error/ErrorAlerta'
import { EnumCanalAtendimentoOrigem } from '@/constants/EnumCanalAtendimentoOrigem'
import { EnumStatusInbox } from '@/constants/EnumStatusInbox'
import { listaDeBaseRotasMSHub } from '@/views/Handler/HandlerEnums'
import mshubPayload from '@/utils/mshubPayload'
import { emitInboxCount } from '@/utils/comandosSdk'

export default {
  components: { ErrorAlerta, ErrorHandler },
  mixins: [status],
  name: 'App',
  data () {
    return {
      ...mapState('SetupApp', ['keepHeightUpdated']),
      origemError: false,
      errorConfig: {
        titulo: 'Não Autorizado',
        texto: 'Você não tem permissão para acessar essa página.',
        showBtn: false
      },
      customHeight: null,
      loading: true,
      resizeTimeout: null,
      inboxCountSyncKey: null,
      inboxCountPendingKey: null
    }
  },
  computed: {
    ...mapState('SetupApp', ['config', 'frameId']),
    ...mapGetters('SetupApp', ['aplicacao', 'bundleWeb', 'estilo', 'instanciaAplicacao']),
    ...mapState('ErrorStore', ['request']),
    ...mapGetters('Login', ['isLogin', 'user']),
    status () {
      return this.$store.state.status
    },
    customStyle () {
      if (this.customHeight) {
        return {
          'min-height': `${this.customHeight}px`,
          'max-height': `${this.customHeight}px`
        }
      }
      return {
        'height': 'auto'
      }
    }
  },
  watch: {
    async 'user.chaveUnica' (chaveUnica, oldChaveUnica) {
      if (!chaveUnica || chaveUnica === oldChaveUnica) {
        return
      }

      await this.syncInboxCount()
    }
  },
  methods: {
    ...mapMutations('SetupApp', ['SET_CONFIG', 'SET_ESQUEMA_DE_COR']),
    emmitLoading (isLoading) {
      let data = {
        uid: window.omnisdkmanager ? window.omnisdkmanager.uid : '',
        origin: 'omnichannel',
        client: this.frameId,
        type: 'command',
        command: 'LOADING',
        data: {
          loading: isLoading
        },
        timestamp: Date.now()
      }
      window.parent.postMessage(data, '*')
      this.loading = isLoading
    },
    loadingStart () {
      this.emmitLoading(true)
      this.loading = true
    },
    loadingEnd () {
      this.emmitLoading(false)
      this.loading = false
    },
    async renew (payload) {
      this.loadingStart()
      await this.$store.dispatch('renew', payload.data)
      this.loadingEnd()
      this.$router.push('/')
    },
    resolveBeneficiarioPayload (payloadBeneficiario) {
      if (!payloadBeneficiario) {
        return null
      }

      if (
        payloadBeneficiario.status === false ||
        payloadBeneficiario.code === 401 ||
        payloadBeneficiario.statusCode === 401
      ) {
        return null
      }

      if (
        payloadBeneficiario.usuarioLogado ||
        (payloadBeneficiario.data && payloadBeneficiario.data.usuarioLogado)
      ) {
        return payloadBeneficiario
      }

      return null
    },
    async initialize (payload) {
      try {
        if (typeof (payload.data) === 'string') {
          payload.data = JSON.parse(payload.data)
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e)
      }

      window.omnisdkmanager = {
        uid: '',
        isInternalLoading: true,
        client: {
          id: ''
        }
      }

      if (payload.uid) {
        window.omnisdkmanager = {
          uid: payload.uid,
          isInternalLoading: (payload.data && payload.data.json && payload.data.json.isInternalLoading !== undefined) ? payload.data.json.isInternalLoading : true,
          client: {
            id: payload.data.id
          }
        }
      }

      const data = {
        uid: payload.uid ? payload.uid : '',
        origin: 'omnichannel',
        type: 'command',
        client: payload.data.id,
        command: 'CREATED',
        data: {
          ...payload.data.json
        },
        timestamp: new Date().getTime()
      }
      window.parent.postMessage(data, '*')
      const payloadInitialConfig = this.checkInitialConfig(payload.data)
      if (!payloadInitialConfig) {
        return this.$router.push('/404')
      }

      const idFuncionalidade = payload.data.json.idFuncionalidade

      const bundleWeb = payload.data.hostname

      const bundle = decodeEmbedData(payload.data.json.bundle)

      this.customHeight = payload.data.json.height

      if (!bundle) {
        return
      }

      const plataforma = payload.data.json.plataforma
      let canal = payload.data.json.canal
      let numeroChamado = ''
      let protocolo = ''
      let origemEmbed = ''
      switch (payload.data.json.canalAtendimentoOrigem) {
        case EnumCanalAtendimentoOrigem.MOSIA_DESK:
          canal = `${payload.data.json.canalAtendimentoOrigem}`
          numeroChamado = payload.data.json.atendimento.callID
          protocolo = payload.data.json.atendimento.protocolo
          origemEmbed = payload.data.json.origemEmbed
          break
        default:
          break
      }

      if (process.env.NODE_ENV !== 'development' && idFuncionalidade > 9000) {
        const validateOrigem = await ConfiguradorResource.validaOrigemFuncionalidade({
          bundle,
          bundleWeb,
          idFuncionalidade
        })

        if (!validateOrigem) {
          this.origemError = true
          return
        }
      }

      const result = await ConfiguradorResource.getSetupApp({
        bundleWeb,
        bundle,
        instanciaAplicacao: payload.data.json.instanciaAplicacao,
        canalAtendimento: canal
      })

      this.SET_CONFIG({
        config: result,
        bundleWeb,
        bundle: result.aplicacao.bundle,
        frameId: payload.data.id,
        plataforma,
        json: payload.data.json,
        canal,
        numeroChamado,
        protocolo,
        origemEmbed
      })

      if (
        Object.prototype.hasOwnProperty.call(payload.data.json, 'authorization') &&
        payload.data.json.authorization
      ) {
        await this.$store.dispatch('Login/initialize', payload.data)

        let beneficiario = payload.data.json.beneficiario

        if (!Object.prototype.hasOwnProperty.call(payload.data.json, 'beneficiario')) {
          const payloadBeneficiario = await PlataformaBeneficiarioResource.carregar()
          beneficiario = this.resolveBeneficiarioPayload(payloadBeneficiario)
        }

        if (beneficiario) {
          await this.$store.dispatch('Login/setBeneficiario', beneficiario)
        } else {
          await this.$store.dispatch('Login/setToken', null)
        }
      }

      const esquemaCor = payload.data.esquemaCor || payload.data.json.esquemaCor || (this.user || {}).esquemaCor
      const isInboxFuncionalidadeInicial = Number(idFuncionalidade) === 1
      await this.aplicarEsquemaCor(esquemaCor, bundle, bundleWeb)
      if (!isInboxFuncionalidadeInicial) {
        await this.syncInboxCount({
          bundle: result.aplicacao.bundle,
          bundleWeb,
          instanciaAplicacao: payload.data.json.instanciaAplicacao
        })
      }

      // Observa a altura do app e informa ao SDK sempre houver alteração
      const resize = new ResizeObserver((entries, observer) => {
        if (this.resizeTimeout) {
          clearTimeout(this.resizeTimeout)
        }
        this.resizeTimeout = setTimeout(() => {
          this.updateHeightIframe(payload.data)
        }, 100)
      })
      resize.observe(document.body)

      // const isAtendente = parseInt(idFuncionalidade) === 63
      // if (
      //   isAtendente &&
      //   Object.prototype.hasOwnProperty.call(payload.data.json, 'atendimento') &&
      //   Object.prototype.hasOwnProperty.call(payload.data.json.atendimento, 'tokenAtendente')
      // ) {
      //   const atendente = ChatApiResource.getAtendente(payload.data.json.atendimento.tokenAtendente)
      // }

      if (
        EnumCanalAtendimentoOrigem.MOSIA_DESK === payload.data.json.canalAtendimentoOrigem &&
        !this.isLogin
      ) {
        this.$router.replace({ path: `/141?redirect=${idFuncionalidade}` })
      } else {
        this.$router.replace({ path: `/${idFuncionalidade}` })
      }

      this.loadingEnd()

      // Detalhe
      if (payload.data.json.idRegistro) {
        return this.$router.push({
          path: `/${payload.data.json.idFuncionalidade}/detalhe`,
          query: {
            idTitulo: payload.data.json.idRegistro,
            codigoTitulo: payload.data.json.idRegistro,
            idNoticia: payload.data.json.idRegistro,
            consulta: { idAgenda: payload.data.json.idRegistro },
            idOcorrencia: payload.data.json.idRegistro
          }
        })
      }
    },
    async receiveMessage (payload) {
      if (payload.origin === 'omnichannelSDK') {
        if (payload.type === 'command') {
          if (payload.command === 'initialize') {
            return this.initialize(payload)
          } else if (payload.command === 'geolocation') {
            await this.setStateGeolocation(payload.data)
          } else {
            const methods = {
              renew: this.renew
            }
            if (Object.prototype.hasOwnProperty.call(methods, payload.command)) {
              try {
                methods[payload.command](payload.data)
              } catch (error) {
                // Ignore error
              }
            }
          }
        }
      }
    },
    async setStateGeolocation (payload) {
      await this.$store.dispatch('RedeCredenciada/setGeolocalizacao', payload)
    },
    async syncInboxCount (options = {}) {
      if (!this.frameId) {
        return
      }

      if (!this.isLogin || !this.user.chaveUnica) {
        this.inboxCountSyncKey = null
        emitInboxCount(this.frameId, 0)
        return
      }

      const bundle = options.bundle || (this.aplicacao || {}).bundle
      const bundleWeb = options.bundleWeb || this.bundleWeb
      const instanciaAplicacao = options.instanciaAplicacao || this.instanciaAplicacao
      const syncKey = `${bundle}:${this.user.chaveUnica}:${instanciaAplicacao}`

      if (!bundle || !bundleWeb || this.inboxCountSyncKey === syncKey || this.inboxCountPendingKey === syncKey) {
        return
      }

      this.inboxCountPendingKey = syncKey

      try {
        const inboxSetup = await ConfiguradorResource.getSetupFuncionalidade({
          bundle,
          bundleWeb,
          idFuncionalidade: 1,
          instanciaAplicacao
        })

        if (!inboxSetup || inboxSetup.status === false || !inboxSetup.rotas) {
          emitInboxCount(this.frameId, 0)
          this.inboxCountSyncKey = syncKey
          return
        }

        const inboxApi = this.createApiRotas(this.aplicacao.rotas, inboxSetup.rotas, 1)

        if (!inboxApi.lista) {
          emitInboxCount(this.frameId, 0)
          this.inboxCountSyncKey = syncKey
          return
        }

        const unreadCount = await this.fetchUnreadInboxCount(inboxApi, bundle)

        if (unreadCount === null) {
          return
        }

        emitInboxCount(this.frameId, unreadCount)
        this.inboxCountSyncKey = syncKey
      } catch (ignored) {
      } finally {
        if (this.inboxCountPendingKey === syncKey) {
          this.inboxCountPendingKey = null
        }
      }
    },
    async fetchUnreadInboxCount (inboxApi, bundle) {
      const inicio = 0
      const qtd = 100
      const result = await inboxApi.lista({
        query: {
          bundle,
          chaveUnica: this.user.chaveUnica,
          statusInbox: EnumStatusInbox.NAO_LIDO,
          inicio,
          qtd
        },
        handlerErrorIgnore: [404]
      })

      if (result && result.status === false && result.code !== 404) {
        return null
      }

      const notificacoes = Array.isArray(result && result.data) ? result.data : []

      return notificacoes.length
    },
    createApiRotas (rotasBase, rotas, idFuncionalidade) {
      const apiRotas = {}

      if (!rotas || !rotasBase) {
        return apiRotas
      }

      Object.keys(rotas).forEach((key) => {
        const rotaConfig = rotas[key]

        if (!rotasBase[rotaConfig.urlBase]) {
          return
        }

        apiRotas[key] = async (opt = {}) => {
          let payload = opt.payload || {}
          const customHeaders = opt.headers || {}
          const queryParams = opt.query || {}
          const params = opt.params
          const notifyFailure = opt.notifyFailure
          const notifySuccess = opt.notifySuccess
          const handlerErrorIgnore = opt.handlerErrorIgnore
          const unauthorizedIgnore = opt.unauthorizedIgnore
          const options = opt.options || {}
          const method = rotaConfig.metodo.toLowerCase()
          let url = rotasBase[rotaConfig.urlBase].rota + (rotaConfig.path || '')

          if (params) {
            Object.keys(params).forEach((paramKey) => {
              url = url.replace(`:${paramKey}`, params[paramKey])
            })
          }

          const headers = {
            'content-type': rotaConfig.contentType,
            instancia_aplicacao: this.instanciaAplicacao,
            ...customHeaders,
            bundle: (this.aplicacao || {}).bundle
          }

          if (listaDeBaseRotasMSHub.indexOf(rotaConfig.urlBase) !== -1) {
            payload = mshubPayload(payload)
            payload.sistema.app.idFuncionalidade = idFuncionalidade
            headers.mshash = this.aplicacao.mshash
          }

          let data

          switch (rotaConfig.contentType) {
            case 'multipart/form-data':
              data = new FormData()
              Object.keys(payload).forEach((payloadKey) => {
                data.append(payloadKey, payload[payloadKey])
              })
              break
            case 'application/json':
            default:
              data = {
                ...payload
              }
          }

          const request = axiosAPI({
            method,
            url,
            data,
            headers,
            params: queryParams
          })

          return axiosAPI.notifyRequest(request, {
            ...options,
            notifyFailure,
            notifySuccess,
            handlerErrorIgnore,
            unauthorizedIgnore
          })
        }
      })

      return apiRotas
    },
    async aplicarEsquemaCor (esquemaCor, bundle, bundleWeb) {
      try {
        const res = await ConfiguradorResource.getEsquemaCor({
          bundle,
          bundleWeb,
          esquemaCor
        })

        if (esquemaCor && !res) {
          await this.aplicarEsquemaCor(undefined, bundle, bundleWeb)
        }

        this.SET_ESQUEMA_DE_COR(res)
      } catch (ignored) {}
    },
    checkInitialConfig (payload) {
      // verifica se as propriedades obrigatórias existem
      const propertiesRequired = ['idFuncionalidade']
      try {
        if (typeof (payload) === 'string') {
          payload = JSON.parse(payload)
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e)
      }

      const hasProp = (obj, prop) => {
        return Object.prototype.hasOwnProperty.call(obj, prop)
      }

      let validParams = true

      for (let i = 0; i < propertiesRequired.length; i++) {
        if (!hasProp(payload.json, propertiesRequired[i])) {
          validParams = false
        }
      }

      if (!validParams) {
        return false
      }

      return payload
    },
    updateHeightIframe (data) {
      if (!this.keepHeightUpdated) return

      const milliseconds = new Date().getTime()
      const height = this.$root.$el.clientHeight
      const payload = {
        uid: window.omnisdkmanager ? window.omnisdkmanager.uid : '',
        origin: 'omnichannel',
        client: data.id,
        type: 'command',
        command: 'UPDATE_HEIGHT',
        data: {
          height: height
        },
        timestamp: milliseconds
      }
      window.parent.postMessage(payload, '*')
    },
    fetchData () {
      // Observa mensagens do SDK
      this.$bindEvent(window, 'message', event =>
        this.receiveMessage(event.data)
      )
    },
    reload () {
      this.$router.go()
    }
  },
  created () {
    // if (process.env.NODE_ENV !== 'production') {
    if (this.$route.path.indexOf('playground') !== -1) {
      this.loading = false
      return
    }
    // }
    // fetch the data when the view is created and the data is
    // already being observed
    this.fetchData()
  }
}
</script>
